<template>
  <div v-if="isOpen" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="header-left">
          <BbIcon size="small" name="backgroundautowidth" />
          <h2 class="modal-title">{{ t('assist.promptCards') }}</h2>
        </div>
        <BbBtn class="close-button" size="small" type="quaternary" @click="closeModal">
          <BbIcon size="small" name="x-small" />
          <span class="close-button-text">Close</span>
        </BbBtn>
      </div>

      <CategoryNavigation
        :categories="categories"
        :active-categories="activeCategories"
        @update:active-category="setActiveCategory"
        @update:search-query="setSearchQuery"
      />

      <div class="modal-content-list">
        <div v-for="category in activeCategories" :key="category.id">
          <div class="category-title-section">
            <h3 class="active-category-title">{{ category.title }}</h3>
          </div>

          <PromptCardsList
            v-if="currentPrompts"
            :prompts="promptsForCategory(category)"
            @select-prompt="handlePromptSelect"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, toRaw, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { isSuggestedCategory } from '~/constants/conversationModels';

import CategoryNavigation from './CategoryNavigation.vue';
import PromptCardsList from '~/components/conversation/quickPrompting/PromptCardsList.vue';
import useQuickPrompts from '~/composables/useQuickPrompts';

export default {
  name: 'PromptCardsModal',
  components: {
    CategoryNavigation,
    PromptCardsList,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'select-prompt', 'select-categories'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { prompts, categories, selectedCategories } = useQuickPrompts();

    const defaultCategories = computed(() =>
      toRaw(selectedCategories.value)?.map((item) => categories.value.find((cat) => cat.id === item)),
    );

    const activeCategories = ref(defaultCategories.value || [isSuggestedCategory]);
    const searchQuery = ref('');
    const currentPrompts = ref([]);

    const closeModal = () => emit('close');

    const setActiveCategory = (category) => {
      const index = activeCategories.value.findIndex((cat) => cat.id === category.id);
      if (index > -1) activeCategories.value.splice(index, 1);
      else activeCategories.value.push(category);

      emit(
        'select-categories',
        activeCategories.value.map((item) => item.id),
      );
    };

    const setSearchQuery = (query) => {
      searchQuery.value = query;
    };

    const handlePromptSelect = (prompt) => {
      emit('select-prompt', prompt);
      closeModal();
    };

    // Returns prompts filtered by category & search query
    const promptsForCategory = (category) => {
      return currentPrompts.value.filter(
        (prompt) =>
          (prompt.cardGroupName === category.title || (category.title === 'Suggested' && prompt.isSuggested)) && // TODO: handle isSuggested
          (!searchQuery.value ||
            prompt.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            prompt.cardGroupName.toLowerCase().includes(searchQuery.value.toLowerCase())),
      );
    };

    watch(
      prompts,
      (newPrompts) => {
        // Merge current prompts with new prompts and deduplicate by id
        const combined = [...toRaw(currentPrompts.value), ...toRaw(newPrompts)];
        const seenIds = new Set();
        let filtered = combined.filter((p) => {
          if (seenIds.has(p.id)) return false;
          seenIds.add(p.id);
          return true;
        });

        // Filter by search query if any
        if (searchQuery.value) {
          const query = searchQuery.value.toLowerCase();
          filtered = filtered.filter(
            (p) => p.title.toLowerCase().includes(query) || p.cardGroupName.toLowerCase().includes(query),
          );
        }

        currentPrompts.value = filtered;
      },
      { immediate: true },
    );

    watch(
      () => props.isOpen,
      (newVal) => {
        if (newVal === true) {
          activeCategories.value = defaultCategories.value || [isSuggestedCategory];
        } else {
          emit('select-categories', [activeCategories.value?.[0]?.id] || [isSuggestedCategory.id]);
        }
      },
      { immediate: true },
    );

    return {
      t,
      activeCategories,
      searchQuery,
      categories,
      currentPrompts,
      closeModal,
      setActiveCategory,
      setSearchQuery,
      handlePromptSelect,
      promptsForCategory,
    };
  },
};
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: $bb-neutral-0;
  border-radius: $bb-spacing-3;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  height: 90vh;
  width: 100%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: $bb-neutral-6;
}

.header-left {
  display: flex;
  align-items: center;
  gap: $bb-spacing-2;
  background-color: $bb-neutral-0;
  padding: $bb-spacing-4;
  border-radius: $bb-spacing-3;
}

.modal-title {
  @include bb-medium-text-styles('sm');
}

.close-button {
  .close-button-text {
    @include bb-medium-text-styles('sm');
  }
}

.category-title-section {
  padding: 0 $bb-spacing-4;
}

.active-category-title {
  @include bb-medium-text-styles('md');
  color: $bb-neutral-3;
  text-transform: uppercase;
}

.modal-content-list {
  overflow: auto;
}

// Responsive adjustments
@media (max-width: 768px) {
  .modal-overlay {
    padding: $bb-spacing-2;
  }

  .modal-content {
    max-height: 95vh;
  }

  .modal-header {
    padding: $bb-spacing-4;
  }
}
</style>
